import { useCallback, useState } from "react"
import { local, type StorageKeyName } from "../lib/storage"

/**
 * Состояние экрана, которое переживает перезагрузку страницы: выбранный сайт,
 * каталог, включённые колонки. Обычный `useState`, только начальное значение
 * читается из хранилища, а новое туда же и пишется.
 *
 * `fallback = null` означает «ещё не выбрано»: экран сам подставит первый
 * подходящий пункт, когда придут справочники.
 */
export function useStoredNumber<T extends number | null>(
	key: StorageKeyName,
	fallback: T
): [number | T, (next: number | T) => void] {
	const [value, setValue] = useState<number | T>(() => local.number(key) ?? fallback)

	const set = useCallback(
		(next: number | T) => {
			setValue(next)
			local.write(key, next)
		},
		[key]
	)

	return [value, set]
}

export function useStoredFlag(
	key: StorageKeyName,
	fallback = false
): [boolean, (next: boolean) => void] {
	const [value, setValue] = useState(() =>
		local.text(key) === null ? fallback : local.flag(key)
	)

	const set = useCallback(
		(next: boolean) => {
			setValue(next)
			local.write(key, next)
		},
		[key]
	)

	return [value, set]
}

/** Свободный текст, который переживает уход с экрана и возврат на него —
 *  например поле поиска в списке. Без валидации: любая строка годится. */
export function useStoredText(
	key: StorageKeyName,
	fallback = ""
): [string, (next: string) => void] {
	const [value, setValue] = useState(() => local.text(key) ?? fallback)

	const set = useCallback(
		(next: string) => {
			setValue(next)
			local.write(key, next)
		},
		[key]
	)

	return [value, set]
}

/** Выбор из фиксированного набора значений (переключатель, сегмент), который
 *  переживает уход с экрана и возврат на него. Значение из хранилища, не
 *  входящее в `allowed`, отбрасывается в пользу `fallback` — так старый или
 *  повреждённый ключ не заводит экран в несуществующее состояние. */
export function useStoredEnum<T extends string>(
	key: StorageKeyName,
	fallback: T,
	allowed: readonly T[]
): [T, (next: T) => void] {
	const [value, setValue] = useState<T>(() => {
		const stored = local.text(key)
		return stored !== null && (allowed as readonly string[]).includes(stored)
			? (stored as T)
			: fallback
	})

	const set = useCallback(
		(next: T) => {
			setValue(next)
			local.write(key, next)
		},
		[key]
	)

	return [value, set]
}
